import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, onUnauthorized } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // status: "checking" (initial), "authenticated", "anonymous"
    const [status, setStatus] = useState("checking");
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setTenant(data.tenant || null);
            setStatus("authenticated");
            return data.user;
        } catch (e) {
            setUser(null);
            setTenant(null);
            setStatus("anonymous");
            return null;
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    useEffect(() => {
        // Global 401 → log out
        return onUnauthorized(() => {
            setUser(null);
            setTenant(null);
            setStatus("anonymous");
        });
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        setStatus("authenticated");
        // Refresh /me to also get tenant info
        const currentUser = await fetchMe();
        if (!currentUser) {
            throw new Error("Inloggen lukte, maar de sessie kon niet worden bevestigd. Probeer het opnieuw.");
        }
        return data.user;
    }, [fetchMe]);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // De HttpOnly-cookie kan alleen door de server betrouwbaar worden
            // gewist. Doe daarom niet alsof uitloggen gelukt is bij netwerkfout.
            console.warn("[auth] logout request failed", e);
            return false;
        }
        setUser(null);
        setTenant(null);
        setStatus("anonymous");
        return true;
    }, []);

    const value = { status, user, tenant, login, logout, refresh: fetchMe };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
